import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Award, Search, RefreshCw, Edit2, Trash2, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

function GradeManagement({ user, onLogout }) {
  const [grades, setGrades] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    grade: '',
    name: '',
    min_ctc: 0,
    max_ctc: 0,
    probation_months: 6,
    notice_period_days: 30
  });

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/org-structure/grades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrades(response.data.grades || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingGrade) {
        await axios.put(`${API}/api/org-structure/grades/${editingGrade.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Grade updated successfully!');
      } else {
        await axios.post(`${API}/api/org-structure/grades`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Grade created successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchGrades();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (grade) => {
    if (!window.confirm(`Are you sure you want to delete grade "${grade.grade}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/org-structure/grades/${grade.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Grade deleted successfully!');
      fetchGrades();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      grade: '',
      name: '',
      min_ctc: 0,
      max_ctc: 0,
      probation_months: 6,
      notice_period_days: 30
    });
    setEditingGrade(null);
  };

  const handleEdit = (grade) => {
    setEditingGrade(grade);
    setFormData({
      grade: grade.grade || '',
      name: grade.name || '',
      min_ctc: grade.min_ctc || 0,
      max_ctc: grade.max_ctc || 0,
      probation_months: grade.probation_months || 6,
      notice_period_days: grade.notice_period_days || 30
    });
    setShowForm(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredGrades = grades.filter(grade =>
    grade.grade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    grade.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort grades by code
  const sortedGrades = [...filteredGrades].sort((a, b) => {
    const aNum = parseInt(a.grade?.replace(/\D/g, '')) || 0;
    const bNum = parseInt(b.grade?.replace(/\D/g, '')) || 0;
    return aNum - bNum;
  });

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Grade & Salary Band Management</h1>
            <p className="text-gray-600 mt-2">Manage organizational grades with CTC bands ({grades.length} total)</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchGrades}
              disabled={dataLoading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: '#2b3380'}}
            >
              <Plus className="w-5 h-5" />
              Add Grade
            </button>
          </div>
        </div>

        {/* ERP Rules */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="font-bold mb-2" style={{color: '#2b3380'}}>🔒 ERP Governance Rules</h3>
          <ul className="space-y-1 text-sm text-gray-700">
            <li>• CTC must fall within grade band</li>
            <li>• Any exception → one-level higher approval required</li>
            <li>• Incentives are outside CTC (Annexure E)</li>
            <li>• Profile NEVER changes → Grade changes on promotion</li>
          </ul>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search grades by code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {dataLoading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading grades...</p>
          </div>
        ) : (
          <>
            {/* Grades Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {sortedGrades.map((grade) => (
                <div key={grade.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Award className="w-10 h-10" style={{color: '#2b3380'}} />
                      <div>
                        <h3 className="text-2xl font-bold" style={{color: '#2b3380'}}>{grade.grade}</h3>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleEdit(grade)} 
                        className="p-2 hover:bg-blue-50 rounded text-blue-600"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(grade)} 
                        className="p-2 hover:bg-red-50 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{grade.name}</p>
                  
                  {grade.min_ctc && grade.max_ctc && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Annual CTC Band</p>
                      <p className="text-lg font-bold" style={{color: '#2b3380'}}>
                        {formatCurrency(grade.min_ctc)} - {formatCurrency(grade.max_ctc)}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t flex gap-4 text-sm text-gray-500">
                    <span>Probation: {grade.probation_months || 6}m</span>
                    <span>Notice: {grade.notice_period_days || 30}d</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Salary Band Table */}
            {sortedGrades.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Complete Salary Band Matrix</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{backgroundColor: '#2b3380'}}>
                      <tr>
                        <th className="px-6 py-3 text-left text-white">Grade</th>
                        <th className="px-6 py-3 text-left text-white">Level Description</th>
                        <th className="px-6 py-3 text-left text-white">Min CTC (Annual)</th>
                        <th className="px-6 py-3 text-left text-white">Max CTC (Annual)</th>
                        <th className="px-6 py-3 text-left text-white">Probation</th>
                        <th className="px-6 py-3 text-left text-white">Notice Period</th>
                        <th className="px-6 py-3 text-left text-white">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedGrades.map((grade, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4 font-bold" style={{color: '#2b3380'}}>{grade.grade}</td>
                          <td className="px-6 py-4">{grade.name}</td>
                          <td className="px-6 py-4 font-medium">{formatCurrency(grade.min_ctc || 0)}</td>
                          <td className="px-6 py-4 font-medium">{formatCurrency(grade.max_ctc || 0)}</td>
                          <td className="px-6 py-4">{grade.probation_months || 6} months</td>
                          <td className="px-6 py-4">{grade.notice_period_days || 30} days</td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button onClick={() => handleEdit(grade)} className="text-blue-600 hover:underline text-sm">
                                Edit
                              </button>
                              <button onClick={() => handleDelete(grade)} className="text-red-600 hover:underline text-sm">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {sortedGrades.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-2">No grades found</p>
                <p className="text-sm text-gray-400">
                  {searchTerm ? 'Try a different search term' : 'Click "Add Grade" to create one'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {editingGrade ? 'Edit Grade' : 'Add Grade'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Grade Code *</label>
                      <input
                        type="text"
                        value={formData.grade}
                        onChange={(e) => setFormData({...formData, grade: e.target.value.toUpperCase()})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., L1, L2, M1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Grade Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Level 1 - Intern / Trainee"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Min CTC (Annual)</label>
                        <input
                          type="number"
                          value={formData.min_ctc}
                          onChange={(e) => setFormData({...formData, min_ctc: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Max CTC (Annual)</label>
                        <input
                          type="number"
                          value={formData.max_ctc}
                          onChange={(e) => setFormData({...formData, max_ctc: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Probation (Months)</label>
                        <input
                          type="number"
                          value={formData.probation_months}
                          onChange={(e) => setFormData({...formData, probation_months: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Notice Period (Days)</label>
                        <input
                          type="number"
                          value={formData.notice_period_days}
                          onChange={(e) => setFormData({...formData, notice_period_days: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 text-white rounded-lg"
                        style={{backgroundColor: loading ? '#cccccc' : '#2b3380'}}
                      >
                        {loading ? 'Saving...' : editingGrade ? 'Update' : 'Create'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default GradeManagement;
